import { Calendar as CalendarIcon, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Calendar } from "@/shared/components/calendar/calendar";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/cn";

interface Props {
  /// Date-only ISO (`YYYY-MM-DD`) or null.
  value: string | null;
  onChange: (next: string | null) => void;
  /// Optional placeholder for the empty state. Default "Pick a date".
  placeholder?: string;
  disabled?: boolean;
  /// Surface a clear-button on the trigger when a value is selected.
  /// Default `true`.
  clearable?: boolean;
  /// Min / max constraints (inclusive). Date-only strings.
  min?: string;
  max?: string;
  /// Optional id for label association (`<Label htmlFor>`).
  id?: string;
  className?: string;
  /// Days to mark in the open calendar, each with the text shown on hover
  /// (a holiday's name, say). Date-only strings.
  marked?: ReadonlyArray<{ date: string; label: string }>;
  /// Month the calendar opens on when nothing is selected yet. Date-only.
  openTo?: string;
}

const TRIGGER_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/// Shared date picker. Popover + the themed `<Calendar>` primitive, looks
/// and behaves the same wherever it appears (todos, 1:1s, HR fields, …).
/// Date-only; for datetimes use `<DateTimePicker>`.
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  clearable = true,
  min,
  max,
  id,
  className,
  marked,
  openTo,
}: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const markedDates = (marked ?? [])
    .map((entry) => parseDateOnly(entry.date))
    .filter((d): d is Date => d !== null);
  const labels = new Map((marked ?? []).map((entry) => [entry.date, entry.label]));
  const initialMonth = openTo ? (parseDateOnly(openTo) ?? undefined) : undefined;
  const selected = value ? parseDateOnly(value) : null;
  const minDate = min ? parseDateOnly(min) : null;
  const maxDate = max ? parseDateOnly(max) : null;
  const label = selected ? TRIGGER_FMT.format(selected) : placeholder;

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "h-9 w-full justify-start gap-2 px-3 text-sm font-normal",
              selected && clearable && "pr-8",
              !selected && "text-text-tertiary",
            )}
          >
            <CalendarIcon aria-hidden="true" className="size-3.5 text-text-tertiary" />
            <span>{label}</span>
          </Button>
        </PopoverTrigger>
        {selected && clearable ? (
          <button
            type="button"
            aria-label="Clear date"
            disabled={disabled}
            onClick={() => onChange(null)}
            className="absolute right-2 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-muted hover:text-text-primary focus-visible:outline-none focus-visible:shadow-ring-focus disabled:opacity-50"
          >
            <X aria-hidden="true" className="size-3" />
          </button>
        ) : null}
        <PopoverContent align="start" className="w-auto border-0 bg-transparent p-0 shadow-none">
          <Calendar
            size="lg"
            selectedDate={selected}
            markedDates={markedDates}
            dayTitle={labels.size > 0 ? (d) => labels.get(formatDateOnly(d)) : undefined}
            initialMonth={initialMonth}
            onSelect={(d) => {
              onChange(formatDateOnly(d));
              setOpen(false);
            }}
            disabledDate={(d) => {
              if (minDate && d < minDate) return true;
              if (maxDate && d > maxDate) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/// Parse a date-only string (`YYYY-MM-DD`) into a local-midnight Date so
/// month / day comparisons against `new Date()` don't drift across DST.
export function parseDateOnly(value: string): Date | null {
  // Accept full ISO too, strip the time portion and rebuild.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/// Format a Date back to `YYYY-MM-DD` (date-only) for the wire.
export function formatDateOnly(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
